// ToastProvider.tsx
import React, { useEffect, useRef, useState } from 'react';
import SuccessIcon from '../assets/images/SuccessIcon';
import ErrorIcon from '../assets/images/ErrorIcon';
import CloseToastIcon from '../assets/images/CloseToastIcon';
import { useNavigate } from 'react-router-dom';
type ToastType = 'success' | 'error' | 'info' | 'warning';


export const showToast = (payload: ToastPayload) => {
    const event = new CustomEvent('app-toast', {
        detail: payload,
    });
    window.dispatchEvent(event);
};

interface ToastPayload {
    message: string;
    type?: ToastType;
    duration?: number;
    path?: string;
    desc?: string;
    id?: string;
}
const ToastProvider: React.FC = () => {
    const [toast, setToast] = useState<ToastPayload | null>();
    const nav = useNavigate();
    const preLanguage = localStorage.getItem("language");
    // One toast at a time: a newer toast replaces the current one and owns the timers, so an
    // earlier short toast can no longer dismiss a later error early.
    const showTimer = useRef<ReturnType<typeof setTimeout>>();
    const hideTimer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        const handler = (e: Event) => {
            const { detail } = e as CustomEvent<ToastPayload>;
            const delayShow = 300;
            const visibleDuration = detail.duration ?? 3000;
            clearTimeout(showTimer.current);
            clearTimeout(hideTimer.current);
            showTimer.current = setTimeout(() => {
                setToast(detail);
                hideTimer.current = setTimeout(() => {
                    setToast(null);
                }, visibleDuration);
            }, delayShow);
        };

        window.addEventListener('app-toast', handler);
        return () => {
            window.removeEventListener('app-toast', handler);
            clearTimeout(showTimer.current);
            clearTimeout(hideTimer.current);
        };
    }, []);

    const getIcon = (type: string) => {
        switch (type) {
            case "success":
                return <SuccessIcon />
            case "error":
                return <ErrorIcon />
        }
    }

    if (!toast) return null;

    return (
        <div
            role={toast.type === 'error' ? 'alert' : 'status'}
            aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
            className={`shadow_popup px-2 py-4 h-fit w-[344px] max-w-[calc(100vw-40px)] bg-[#FFFFFF] border border-[#F3F3F3] rounded-2xl overflow-hidden absolute top-[92px] right-[20px] z-[150]  toast toast-${toast.type ?? 'info'}`}
        >
            <div className='flex items-start gap-x-4'>
                <div className='w-[24px] h-[24px] flex items-center justify-center'>
                    {getIcon(toast.type || '')}
                </div>
                <div className='flex-1 text-[14px] font-inter'>
                    <div className='text-[14px] font-medium text-[#222525]'>{toast.message}</div>
                    {toast.desc &&
                        toast.path ?
                        (<div className='cursor-pointer' onClick={() => { nav(`/${preLanguage}/${toast.path}/${toast.id}`); setToast(null) }}>
                            <p dangerouslySetInnerHTML={{ __html: toast.desc }} />
                        </div>)
                        :
                        (<p dangerouslySetInnerHTML={{ __html: toast.desc ?? '' }} />)
                    }
                </div>
                <div role='button' aria-label='Close' tabIndex={0} className='w-[24px] h-[24px] flex items-center justify-center cursor-pointer' onClick={() => { setToast(null) }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setToast(null) }}>
                    <CloseToastIcon />
                </div>
            </div>
        </div>
    );
};

export default ToastProvider;
